import {
  GencowValidationError,
  createRealtimeQuerySubscriptionToken,
  getRealtimeSubscriptionDef,
  normalizeRealtimeQuerySubscriptionEpoch,
  parseArgs,
  resolveRealtimeQueryScopeHash,
  resolveRealtimeQuerySubscriptionKey,
  type GencowCtx,
} from "@gencow/core";

const DEFAULT_QUERY_GRANT_TTL_SECONDS = 300;
const MAX_QUERY_GRANT_TTL_SECONDS = 3600;

type RouteContext = {
  req: {
    json(): Promise<unknown>;
  };
  header(name: string, value: string): void;
  json(body: unknown, status?: number): unknown;
};

type RouteRegistrar = {
  post(path: string, handler: (context: RouteContext) => Promise<unknown> | unknown): void;
};

function resolveStatus(error: unknown): number {
  if (error instanceof GencowValidationError) return 400;
  if (!error || typeof error !== "object") return 403;
  const status = (error as { status?: unknown; statusCode?: unknown }).status;
  const statusCode = (error as { statusCode?: unknown }).statusCode;
  const candidate = typeof status === "number" ? status : typeof statusCode === "number" ? statusCode : 403;
  return Number.isInteger(candidate) && candidate >= 400 && candidate <= 499 ? candidate : 403;
}

function resolveTtlSeconds(value: unknown): number {
  if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) {
    return DEFAULT_QUERY_GRANT_TTL_SECONDS;
  }
  return Math.floor(Math.min(value, MAX_QUERY_GRANT_TTL_SECONDS));
}

function resolveAuthorizeErrorBody(error: unknown): { body: { error: string }; status: number } {
  const status = resolveStatus(error);
  if (status === 400) {
    const message = error instanceof Error && error.message ? error.message : "Invalid query arguments";
    return { body: { error: message }, status };
  }
  if (status === 401) return { body: { error: "Authentication required" }, status };
  return { body: { error: "Realtime query authorization failed" }, status };
}

export function registerRealtimeQueryRoute(params: {
  app: RouteRegistrar;
  appName: string;
  internalToken: string;
  buildCtx(context: unknown): GencowCtx;
  nowMs?: () => number;
}): void {
  params.app.post("/api/realtime/query/authorize", async (c) => {
    c.header("Cache-Control", "no-store");
    c.header("Pragma", "no-cache");

    const body = (await c.req.json().catch(() => ({}))) as { query?: unknown; args?: unknown };
    if (typeof body.query !== "string" || body.query.length === 0) {
      return c.json({ error: "query required" }, 400);
    }

    const def = getRealtimeSubscriptionDef(body.query);
    if (!def) {
      return c.json({ error: "Realtime query subscription not found" }, 404);
    }
    if (def.policy === "public") {
      return c.json({ error: "Public realtime query does not require authorization" }, 400);
    }

    const ctx = params.buildCtx(c);
    const user = ctx.auth.getUserIdentity();
    if (!user) {
      return c.json({ error: "Authentication required" }, 401);
    }

    try {
      const args = parseArgs(def.argsSchema, body.args);
      const result = def.authorize ? await def.authorize(ctx, args) : undefined;
      const subscriptionKey = resolveRealtimeQuerySubscriptionKey({ query: body.query, args, result });
      if (!subscriptionKey || result === false || result === null) {
        return c.json({ error: "Realtime query authorization failed" }, 403);
      }

      const objectResult = result && typeof result === "object" && !Array.isArray(result) ? result : null;
      const ttlSeconds = objectResult ? resolveTtlSeconds(objectResult.ttlSeconds) : DEFAULT_QUERY_GRANT_TTL_SECONDS;
      const epoch = objectResult ? normalizeRealtimeQuerySubscriptionEpoch(objectResult.epoch) : undefined;
      const scopeHash = resolveRealtimeQueryScopeHash({
        query: body.query,
        args,
        subscriptionKey,
        result,
      });
      const nowMs = params.nowMs?.() ?? Date.now();
      const token = createRealtimeQuerySubscriptionToken({
        appName: params.appName,
        query: body.query,
        subscriptionKey,
        args,
        scopeHash,
        policy: def.policy,
        userId: String(user.id),
        secret: params.internalToken,
        ttlSeconds,
        nowMs,
        epoch,
      });

      return c.json({
        token,
        query: body.query,
        subscriptionKey,
        policy: def.policy,
        userId: String(user.id),
        expiresAt: nowMs + ttlSeconds * 1000,
        ...(epoch ? { epoch } : {}),
      });
    } catch (error) {
      const { body: errorBody, status } = resolveAuthorizeErrorBody(error);
      return c.json(errorBody, status);
    }
  });
}
